import { t } from "i18next";
import React from "react";
import Highlighter from "react-highlight-words";
import i18next from "src/i18n/config";
import { useDispatch } from "src/store";
import { TextLink } from "src/ui/shared/TextLink";
import { makeStyles } from "tss-react/mui";

import { showDetails } from "@dashboard/devices/store";
import { Group } from "@dashboard/devices/types";
import { Tooltip } from "@shared/Tooltip";
import { useIsClipped } from "@shared/useIsClipped";
import { ColumnDef, ColumnFiltersState, ExpandedState, FilterFnOption, Row } from "@tanstack/react-table";

import { ActionCell } from "./components/table/ActionCell";
import { ComplianceStatusIcon } from "./components/table/ComplianceStatus";
import DevicesCountCell from "./components/table/DevicesCountCell";
import { ExpandedCell, ExpandedHeader } from "./components/table/Expander";
import { OnlineStatusIcon } from "./components/table/OnlineStatus";
import TableCell from "./components/table/TableCell";
import { getParentGroups } from "./utils";

const useStyles = makeStyles()((theme) => ({
    highlightedText: {
        backgroundColor: theme.palette.emteria.warning,
    },
    description: {
        display: "block",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
}));

const DescriptionCell = React.memo(({ value, globalFilter }: { value: string; globalFilter?: string }) => {
    const { classes } = useStyles();
    const { ref, clipped } = useIsClipped(value);

    return (
        <Tooltip title={value} hide={!clipped} disableChildrenWrapper>
            <span ref={ref} className={classes.description}>
                <Highlighter
                    searchWords={globalFilter ? [globalFilter] : []}
                    textToHighlight={value}
                    autoEscape
                    highlightClassName={classes.highlightedText}
                />
            </span>
        </Tooltip>
    );
});
DescriptionCell.displayName = "DescriptionCell";

const NameCell = ({ id, value, globalFilter }: { id: number; value: string; globalFilter?: string }) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();

    const showGroup: React.MouseEventHandler<HTMLElement> = (event) => {
        // The name sits inside the expander of a group with subgroups, whose click toggles them open.
        event.stopPropagation();
        dispatch(showDetails({ type: "group", selectedId: id }));
    };

    return (
        <TextLink
            onClick={showGroup}
            tooltip={i18next.t("pages:groupPage.tooltip.showGroupDetails")}
            truncatedTooltip={value}
            sx={{ maxWidth: 280 }}
        >
            <Highlighter
                searchWords={globalFilter ? [globalFilter] : []}
                textToHighlight={value ?? ""}
                autoEscape
                highlightClassName={classes.highlightedText}
            />
        </TextLink>
    );
};

export interface GroupOverviewData {
    name: string;
    description: string;
    onlineDevicePercentage: string;
    compliantDevicePercentage: string;
    numberOfDevices: number;
    id: number;
    parentId: number | null;
    children: GroupOverviewData[];
}

function addChildrenProperty(data: GroupOverviewData[]) {
    const map = new Map<number, GroupOverviewData>();

    data.forEach((item) => {
        map.set(item.id, { ...item, children: [] });
    });

    data.forEach((item) => {
        if (item.parentId !== null) {
            const parent = map.get(item.parentId);
            const child = map.get(item.id);
            if (parent && child) {
                parent.children.push(child);
            }
        }
    });

    const groupIds = data.map((item) => item.id);
    return Array.from(map.values()).filter((item) => item.parentId === null || !groupIds.includes(item.parentId));
}

// Whole percent, but 0% and 100% only when exactly true: one offline device among a thousand must not read as
// "100%" next to an "all devices are online" icon.
const toWholePercent = (part: number, total: number) => {
    const rounded = Math.round((part / total) * 100);
    if (rounded === 100 && part < total) {
        return 99;
    }
    if (rounded === 0 && part > 0) {
        return 1;
    }
    return rounded;
};

export const getGroupsTableData = (groups: Group[]): GroupOverviewData[] => {
    const groupArray: GroupOverviewData[] = [];

    const emptyStatistics = {
        deviceCount: 0,
        connectionStatus: { online: 0, offline: 0 },
        policyStatus: { compliant: 0, nonCompliant: 0 },
    };

    groups.forEach((group) => {
        const stats = group.statistics ?? emptyStatistics;
        const numberOfDevices = stats.deviceCount;
        const devicesWithConnectionStatus = stats.connectionStatus.online + stats.connectionStatus.offline;
        const onlineDevicePercentage =
            devicesWithConnectionStatus > 0
                ? `${toWholePercent(stats.connectionStatus.online, devicesWithConnectionStatus)}%`
                : "";
        const devicesWithPolicyStatus = stats.policyStatus.compliant + stats.policyStatus.nonCompliant;
        const compliantDevicePercentage = devicesWithPolicyStatus
            ? `${toWholePercent(stats.policyStatus.compliant, devicesWithPolicyStatus)}%`
            : "";

        const newGroupData = {
            name: group.name,
            description: group.description || "-",
            onlineDevicePercentage,
            compliantDevicePercentage,
            numberOfDevices,
            id: group.id,
            parentId: group.parentId,
            children: [],
        };

        groupArray.push(newGroupData);
    });

    return addChildrenProperty(groupArray);
};

export const getGroupIdsFromNameColumnFilter = (columnFilters: ColumnFiltersState): number[] => {
    const value = columnFilters.find((filter) => filter.id === "name")?.value;
    if (!Array.isArray(value) || value.length === 0) {
        return [];
    }
    return value.map((id) => Number(id));
};

const getClosestSelectedAncestorId = (
    groupId: number,
    selectedIds: Set<number>,
    groupsById: Map<number, Group>,
): number | null => {
    let candidateParentId = groupsById.get(groupId)?.parentId ?? null;
    while (candidateParentId !== null && !selectedIds.has(candidateParentId)) {
        candidateParentId = groupsById.get(candidateParentId)?.parentId ?? null;
    }
    return candidateParentId;
};

export const buildGroupTableDataForSelectionFilter = (
    fullTree: GroupOverviewData[],
    allGroups: Group[],
    selectedIds: number[],
): GroupOverviewData[] => {
    const selectedSet = new Set(selectedIds);
    const groupsById = new Map(allGroups.map((group) => [group.id, group]));
    const overviewById = new Map<number, GroupOverviewData>();
    const orderedSelectedIds: number[] = [];

    const addGroup = (group: GroupOverviewData) => {
        overviewById.set(group.id, group);
        if (selectedSet.has(group.id)) {
            orderedSelectedIds.push(group.id);
        }
        group.children.forEach(addGroup);
    };
    fullTree.forEach(addGroup);

    const rows: GroupOverviewData[] = [];
    for (const id of orderedSelectedIds) {
        const sourceRow = overviewById.get(id);
        if (!sourceRow) {
            continue;
        }
        rows.push({
            ...sourceRow,
            parentId: getClosestSelectedAncestorId(id, selectedSet, groupsById),
            children: [],
        });
    }

    return addChildrenProperty(rows);
};

export const getExpandedStateForGroup = (
    targetGroupId: number,
    groups: Group[],
    baseExpanded: ExpandedState,
): Record<string, boolean> => {
    const parentGroups = getParentGroups(targetGroupId, groups);

    if (parentGroups.length === 0) {
        return baseExpanded === true ? {} : baseExpanded;
    }

    const expandedState = baseExpanded === true ? {} : { ...baseExpanded };

    parentGroups.forEach((group) => {
        expandedState[String(group.id)] = true;
    });

    return expandedState;
};

// Pagination slices top-level rows only, so a nested group shares a page with its root ancestor.
export const findRootRowIndexForGroup = (rows: Row<GroupOverviewData>[], groupId: number): number => {
    const containsGroup = (row: Row<GroupOverviewData>): boolean =>
        row.original.id === groupId || row.subRows.some(containsGroup);

    return rows.findIndex(containsGroup);
};

export const customGlobalFilterFn: FilterFnOption<GroupOverviewData> = (row, _columnId, filterValue) => {
    if (!filterValue) {
        return true;
    }
    const normalizedFilterValue = String(filterValue).toLowerCase();

    const checkRowMatch = (rowData: GroupOverviewData): boolean => {
        const rowMatches = Object.values(rowData).some((value) =>
            typeof value === "string" || typeof value === "number"
                ? String(value).toLowerCase().includes(normalizedFilterValue)
                : false,
        );

        if (rowData.children && rowData.children.length > 0) {
            return rowMatches || rowData.children.some(checkRowMatch);
        }

        return rowMatches;
    };

    return checkRowMatch(row.original);
};

export const getColumns = (globalFilter?: string): ColumnDef<GroupOverviewData, unknown>[] => [
    {
        header: (info) => <ExpandedHeader title={t("pages:groupPage.table.header.name")} {...info} />,
        accessorKey: "name",
        enableSorting: true,
        cell: (info) => (
            <ExpandedCell {...info} shouldOpenSidePanel>
                <NameCell id={info.row.original.id} value={info.getValue<string>()} globalFilter={globalFilter} />
            </ExpandedCell>
        ),
        filterFn: (row, _columnId, filterValue: string[]) => {
            if (filterValue.length === 0) {
                return true;
            }
            return filterValue.includes(String(row.original.id));
        },
    },
    {
        header: t("common:label.description"),
        accessorKey: "description",
        cell: (info) => <DescriptionCell value={info.getValue<string>()} globalFilter={globalFilter} />,
        enableSorting: true,
        meta: { fluid: true, fluidMinWidth: "24ch" },
    },
    {
        header: t("pages:groupPage.table.header.devices"),
        accessorKey: "numberOfDevices",
        cell: (info) => <DevicesCountCell {...info} />,
        enableSorting: true,
        sortingFn: "basic",
    },
    {
        header: t("common:status.online"),
        accessorKey: "onlineDevicePercentage",
        cell: (info) => (
            <TableCell {...info}>
                <OnlineStatusIcon {...info} />
            </TableCell>
        ),
        enableSorting: true,
        sortingFn: "alphanumeric",
    },
    {
        header: t("pages:groupPage.table.header.compliance"),
        accessorKey: "compliantDevicePercentage",
        cell: (info) => (
            <TableCell {...info}>
                <ComplianceStatusIcon {...info} />
            </TableCell>
        ),
        enableSorting: true,
    },
    {
        header: "",
        accessorKey: "action",
        cell: (info) => <ActionCell {...info} />,
        enableSorting: true,
    },
];
